import { Typography } from 'antd';
import React, { useState } from 'react';
import { ReactComponent as AlertRedIcon } from '../../../../Assets/yesbank/alert-red.svg';
import { ReactComponent as LoaderIcon } from '../../../../Assets/loader.svg';
import styles from './index.module.css';

type Props = {
  schemeData: any;
  isFetching?: boolean;
  isUnableToFetch?: boolean;
};

const MutualFundCard: React.FC<Props> = ({ schemeData, isFetching = false, isUnableToFetch = false }) => {
  const [isAccountsExpanded, setIsAccountsExpanded] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [logoExtension, setLogoExtension] = useState<'png' | 'jpg'>('png');

  const schemeName = schemeData?.mutualFundName || 'Unknown Scheme';
  const accounts = schemeData?.folioNumbers || [];
  const totalUnits = parseFloat(schemeData?.units || 0);
  const totalCurrentValue = parseFloat(schemeData?.currentValue || 0);
  const nav = schemeData?.nav || 0;

  // Logo URL - for fetching/unable to fetch, derive from scheme name; for normal, use API logo
  const getLogoUrl = () => {
    if (isFetching || isUnableToFetch) {
      const logoName = schemeName.replace(/ /g, '_');
      return `https://logo.ignosis.ai/fund/${logoName}.${logoExtension}`;
    }
    if (schemeData?.logo) {
      return `https://logo.ignosis.ai/fund/${schemeData.logo}`;
    }
    return null;
  };

  const handleLogoError = () => {
    if ((isFetching || isUnableToFetch) && logoExtension === 'png') {
      setLogoExtension('jpg');
    } else {
      setLogoError(true);
    }
  };

  const formatNumber = (num: number | string) => {
    const number = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(number)) return '0';
    return number.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  const renderAccounts = () => {
    const validAccounts = accounts.filter((acc: any) => acc);
    if (validAccounts.length === 0) return null;

    const getDisplay = (acc: string) => `** ${acc?.slice(-4) || '****'}`;

    // First 2 always shown
    const firstTwo = validAccounts.slice(0, 2);
    const remaining = validAccounts.slice(2);

    return (
      <>
        <div className={styles.folioRow}>
          {firstTwo.map((acc: string, index: number) => (
            <div key={index} className={styles.folioTag}>{getDisplay(acc)}</div>
          ))}
          {remaining.length > 0 && !isAccountsExpanded && (
            <div
              className={styles.folioTagMore}
              onClick={(e) => { e.stopPropagation(); setIsAccountsExpanded(true); }}
            >
              +{remaining.length} accounts
            </div>
          )}
        </div>
        {isAccountsExpanded && remaining.length > 0 && (
          <div className={styles.folioRow}>
            {remaining.map((acc: string, index: number) => (
              <div key={index} className={styles.folioTag}>{getDisplay(acc)}</div>
            ))}
            <div
              className={styles.folioTagMore}
              onClick={(e) => { e.stopPropagation(); setIsAccountsExpanded(false); }}
            >
              Show less
            </div>
          </div>
        )}
      </>
    );
  };

  const logoUrl = getLogoUrl();

  const renderLogo = () => {
    if (logoUrl && !logoError) {
      return (
        <img
          src={logoUrl}
          alt={schemeName}
          className={styles.schemeLogoImage}
          onError={handleLogoError}
        />
      );
    }
    return (
      <svg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <rect width='32' height='32' rx='8' fill='#F6F6F8' />
        <path d='M16 9L19 15H13L16 9Z' fill='#002EDC' />
        <rect x='13' y='17' width='6' height='6' fill='#002EDC' />
      </svg>
    );
  };

  return (
    <div className={styles.mfCard}>
      <div className={styles.mfCardTop}>
        <div className={styles.mfCardHeader}>
          <div className={styles.schemeIcon}>{renderLogo()}</div>
          <div className={styles.schemeInfo}>
            <div className={styles.schemeHeader}>
              <Typography.Text className={styles.schemeName}>{schemeName}</Typography.Text>
            </div>
            <div className={styles.folioTags}>{renderAccounts()}</div>
          </div>
        </div>
      </div>

      <div className={styles.divider}></div>

      {isFetching ? (
        <div className={styles.fetchingMessage}>
          <LoaderIcon className={styles.spinLoader} />
          <Typography.Text className={styles.fetchingText}>
            Fetching data. This may take a few moments          </Typography.Text>
        </div>
      ) : isUnableToFetch ? (
        <div className={styles.unableToFetchMessage}>
          <AlertRedIcon />
          <Typography.Text className={styles.unableToFetchText}>
            Unable to fetch data
          </Typography.Text>
        </div>
      ) : (
        <div className={styles.mfCardDetails}>
          <div className={styles.detailItem}>
            <Typography.Text className={styles.detailLabel}>Units</Typography.Text>
            <Typography.Text className={styles.detailValue}>{formatNumber(totalUnits)}</Typography.Text>
          </div>
          <div className={styles.detailDivider}></div>
          <div className={styles.detailItem}>
            <Typography.Text className={styles.detailLabel}>NAV</Typography.Text>
            <Typography.Text className={styles.detailValue}>{formatNumber(nav)}</Typography.Text>
          </div>
          <div className={styles.detailDivider}></div>
          <div className={styles.detailItem}>
            <Typography.Text className={styles.detailLabel}>Current Value</Typography.Text>
            <Typography.Text className={styles.detailValue}>{formatNumber(totalCurrentValue)}</Typography.Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default MutualFundCard;
